import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { DryRunResponse } from '../types/retentionAdmin';

interface PurgeConfirmDialogProps {
  policyId: number;
  policyName: string;
  targetTable: string;
  dryRunResult: DryRunResponse | null;
  isPending: boolean;
  onConfirm: (confirmName: string, dryRunToken: string) => void;
  onCancel: () => void;
  onRunDryRun: () => void;
}

/**
 * PurgeConfirmDialog — ★ 2단계 확인 (파괴적 작업)
 * 1단계: dry-run 결과 표시
 * 2단계: 정책명 정확 타이핑 일치 시 [실행] 활성화
 */
export function PurgeConfirmDialog({
  policyName,
  targetTable,
  dryRunResult,
  isPending,
  onConfirm,
  onCancel,
  onRunDryRun,
}: PurgeConfirmDialogProps) {
  const { t } = useTranslation();
  const [confirmInput, setConfirmInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const isNameMatch = confirmInput === policyName;
  const canRun = isNameMatch && dryRunResult !== null && !isPending;

  const pct = dryRunResult ? Math.round(dryRunResult.ratio * 1000) / 10 : 0;
  const maxPct = dryRunResult ? Math.round(dryRunResult.maxDeleteRatio * 100) : 50;
  const exceeds = dryRunResult?.ratioExceedsGuard ?? false;
  const barWidth = dryRunResult
    ? Math.min(dryRunResult.ratio / dryRunResult.maxDeleteRatio, 1) * 100
    : 0;

  // ESC 닫기
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onCancel]);

  // focus trap — 열리면 input 또는 취소 버튼 포커스
  useEffect(() => {
    if (dryRunResult) {
      inputRef.current?.focus();
    } else {
      cancelRef.current?.focus();
    }
  }, [dryRunResult]);

  function handleSubmit() {
    if (!canRun || !dryRunResult) return;
    onConfirm(confirmInput, dryRunResult.dryRunToken);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="purge-dialog-title"
      aria-describedby="purge-dialog-desc"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.55)',
      }}
    >
      <div
        style={{
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          width: 480,
          maxWidth: '95vw',
          boxShadow: 'var(--shadow-xl)',
          border: '2px solid var(--color-danger)',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <h2
            id="purge-dialog-title"
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              color: 'var(--color-danger)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            <span aria-hidden="true">&#9888;</span> {t('retentionAdmin.purge.title')}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label="닫기"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 'var(--font-size-xl)',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              lineHeight: 1,
            }}
          >
            &#x2715;
          </button>
        </div>

        {/* 정책 정보 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 'var(--spacing-1) var(--spacing-3)',
            marginBottom: 'var(--spacing-4)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          <span style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>
            {t('retentionAdmin.purge.confirmLabel')}
          </span>
          <span style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-mono)' }}>
            {policyName}
          </span>
          <span style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>
            {t('retentionAdmin.purge.targetLabel')}
          </span>
          <span style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-mono)' }}>
            {targetTable}
          </span>
        </div>

        {/* dry-run 결과 또는 요청 */}
        {dryRunResult === null ? (
          <div
            id="purge-dialog-desc"
            style={{
              padding: 'var(--spacing-4)',
              background: 'var(--color-surface-muted)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--spacing-4)',
              textAlign: 'center',
            }}
          >
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              {t('retentionAdmin.purge.dryRunRequired')}
            </p>
            <button
              type="button"
              onClick={onRunDryRun}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('retentionAdmin.purge.button.dryRunFirst')}
            </button>
          </div>
        ) : (
          <div
            id="purge-dialog-desc"
            style={{
              padding: 'var(--spacing-3)',
              background: exceeds ? 'var(--color-surface-muted)' : 'var(--color-surface-muted)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--spacing-4)',
              border: `1px solid ${exceeds ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
            }}
          >
            <p
              style={{
                fontWeight: 700,
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {t('retentionAdmin.purge.previewLabel')}
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 700,
                color: exceeds ? 'var(--color-danger)' : 'var(--color-text-primary)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {dryRunResult.matched.toLocaleString()}건 삭제 예정 (전체{' '}
              {dryRunResult.total.toLocaleString()}건의 {pct}%)
            </p>

            {/* 비율 바 */}
            <div
              style={{
                height: 8,
                background: 'var(--color-surface-elevated)',
                borderRadius: 'var(--radius-full, 9999px)',
                overflow: 'hidden',
                marginBottom: 'var(--spacing-2)',
              }}
              aria-hidden="true"
            >
              <div
                style={{
                  height: '100%',
                  width: `${barWidth}%`,
                  background: exceeds ? 'var(--color-danger)' : 'var(--color-brand-primary)',
                  borderRadius: 'var(--radius-full, 9999px)',
                }}
              />
            </div>

            {exceeds ? (
              <p
                role="alert"
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-danger)',
                  fontWeight: 600,
                }}
              >
                {t('retentionAdmin.purge.guardWarning', {
                  ratio: `${pct}%`,
                  max: `${maxPct}%`,
                })}
              </p>
            ) : (
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-success, #16a34a)',
                }}
              >
                {t('retentionAdmin.purge.guardOk', { max: `${maxPct}%` })}
              </p>
            )}
          </div>
        )}

        {/* 경고 문구 */}
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-danger)',
            fontWeight: 600,
            marginBottom: 'var(--spacing-2)',
          }}
        >
          {t('retentionAdmin.purge.warning')}
        </p>

        {/* 정책명 입력 */}
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label
            htmlFor="purge-confirm-input"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-1)',
            }}
          >
            {t('retentionAdmin.purge.confirmHint')}
          </label>
          <input
            id="purge-confirm-input"
            ref={inputRef}
            type="text"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
            placeholder={t('retentionAdmin.purge.confirmPlaceholder')}
            aria-invalid={confirmInput.length > 0 && !isNameMatch}
            aria-describedby="purge-name-error"
            disabled={isPending}
            style={{
              width: '100%',
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: `1px solid ${
                confirmInput.length > 0 && !isNameMatch
                  ? 'var(--color-danger)'
                  : 'var(--color-surface-border)'
              }`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-mono)',
              boxSizing: 'border-box',
            }}
          />
          {confirmInput.length > 0 && !isNameMatch && (
            <p
              id="purge-name-error"
              role="alert"
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-danger)',
                marginTop: 'var(--spacing-1)',
              }}
            >
              {t('retentionAdmin.error.RETENT-422-001')}
            </p>
          )}
        </div>

        {/* 버튼 영역 */}
        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-2)',
            justifyContent: 'flex-end',
          }}
        >
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={isPending}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('retentionAdmin.purge.button.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canRun}
            aria-disabled={!canRun}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: canRun ? 'var(--color-danger)' : 'var(--color-surface-muted)',
              color: canRun ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: canRun ? 'pointer' : 'not-allowed',
              opacity: canRun ? 1 : 0.5,
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {isPending
              ? '...'
              : dryRunResult
              ? t('retentionAdmin.purge.button.run', {
                  matched: dryRunResult.matched.toLocaleString(),
                })
              : t('retentionAdmin.purge.button.run', { matched: '?' })}
          </button>
        </div>
      </div>
    </div>
  );
}
